'use client';

import * as React from 'react';
import {
  LayoutDashboard,
  Calendar,
  Users,
  UserCog,
  TrendingUp,
  Settings,
  HelpCircle,
  Sparkles,
  Send,
  HeartHandshake,
} from 'lucide-react';

import { NavMain } from './NavMain';
import { NavProjects } from './NavProjects';
import { NavUser } from './NavUser';
import { TeamSwitcher } from './TeamSwitcher';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from '@/components/ui/sidebar';
import { useAuthStore, type PlanType } from '@/store/useAuthStore';
import { useAuthContext } from '@/hooks/useAuth';
import { useShopContext } from '@/hooks/useShopContext';
import { useFeatureAccess } from '@/components/plan/FeatureGate';
import { isRepeatEnabled } from '@/lib/repeatGate';
import { useIsRepeatFirstOwner } from '@/hooks/repeat/useIsRepeatFirstOwner';

const PLAN_DISPLAY_LABEL: Record<PlanType, string> = {
  free: 'Free',
  light: 'Light',
  standard: 'Standard',
  premium: 'Premium',
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  // 認証情報: React Context（単一の真実の源）から取得
  const { user } = useAuthContext();

  // 店舗名: useShopContext (shops.name) から取得 (#2953)
  // fallback: currentStore 未取得 (初期化中 / fetch 失敗) 時は固定ブランド 'Ripia'
  // NOTE: useAuthContext の User 型 (services/authService.ts L215) は store オブジェクトを
  // 持たず storeId のみ提供のため、middle fallback (user.store.name) は不可。
  const { currentStore } = useShopContext();
  const storeName = currentStore?.name ?? 'Ripia';
  const currentPlan = useAuthStore((state) => state.currentPlan);

  // Issue #3292/#3315: Free plan で plan 不一致 nav を非表示にする (4 層 defense L1)
  // - follow_up: Light+ 機能 → Free 非表示
  // - ai_analytics: Free 含む全 plan (SSOT: docs/core/plan-comparison.md:91) → 全 plan 表示
  // React rule-of-hooks 遵守のため、 component 上部で feature ごとに個別取得
  const repeatFeatureAccess = useFeatureAccess('repeat');
  const featureAccess: Record<string, boolean> = {
    follow_up: useFeatureAccess('follow_up'),
    repeat: isRepeatEnabled() && repeatFeatureAccess,
    ai_analytics: useFeatureAccess('ai_analytics'),
  };

  // #4038 / HAM-3471 案B: repeat-first owner には /dashboard 導線を出さない
  // （gate で /repeat へ bounce されるだけの導線を隠す cosmetic）。staff は維持。
  const isRepeatFirstOwner = useIsRepeatFirstOwner();

  // メインナビゲーション
  const navMain: Array<{
    title: string;
    url: string;
    icon: typeof LayoutDashboard;
    isActive?: boolean;
    feature?: string;
  }> = [
    {
      title: 'ダッシュボード',
      url: '/dashboard',
      icon: LayoutDashboard,
      isActive: true,
    },
    {
      title: '予約管理',
      url: '/reservations',
      icon: Calendar,
    },
    {
      title: '顧客管理',
      url: '/customers',
      icon: Users,
    },
    {
      title: 'フォローアップ',
      url: '/follow-up',
      icon: Send,
      feature: 'follow_up',
    },
    {
      title: 'リピート作成',
      url: '/repeat',
      icon: HeartHandshake,
      feature: 'repeat',
    },
    {
      title: 'スタッフ',
      url: '/staff',
      icon: UserCog,
    },
    {
      title: '売上分析',
      url: '/sales',
      icon: TrendingUp,
    },
    {
      title: 'AI分析',
      url: '/ai-analysis',
      icon: Sparkles,
      feature: 'ai_analytics',
    },
  ];

  // Free plan 時は plan 不一致 nav を除外 (feature 不在 item は全 plan 表示)
  // 案B: repeat-first owner のみ /dashboard 項目を非表示 (staff / 非 repeat owner は維持)
  const visibleNavMain = navMain.filter((item) => {
    if (item.url === '/dashboard' && isRepeatFirstOwner) {
      return false;
    }
    return !item.feature || featureAccess[item.feature];
  });

  // 下部ナビゲーション（設定・ヘルプ） — Issue #2909: 「店舗設定」削除、/settings に統合
  const bottomNav = [
    {
      title: '設定',
      url: '/settings',
      icon: Settings,
    },
    {
      title: 'ヘルプ',
      url: '/help',
      icon: HelpCircle,
    },
  ];

  // 店舗情報（単一 tenant 表示用）
  const teams = [
    {
      name: storeName || 'Ripia',
      plan: PLAN_DISPLAY_LABEL[currentPlan], // PlanType → display label (type-safe)
    },
  ];

  return (
    <Sidebar collapsible="icon" variant="inset" {...props}>
      <SidebarHeader>
        <TeamSwitcher teams={teams} />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={visibleNavMain} />
        <NavProjects items={bottomNav} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={user} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
